import { transition } from '@helpers/animate';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { Device, from, until } from '@helpers/media';
import { h3, paragraphLarge, paragraphMedium, paragraphSmall } from '@helpers/typography';
import DropdownStyles from '@stories/Components/Inputs/Dropdown/Dropdown.styles';
import FiltersStyles from '@stories/Components/Listings/Filters/Filters.styles';
import SectionWrapper from '@stories/Components/Misc/SectionWrapper/SectionWrapper';
import SectionWrapperStyles from '@stories/Components/Misc/SectionWrapper/SectionWrapper.styles';
import { transparentize } from 'polished';
import styled from 'styled-components';
const Container = styled.div `
  display: flex;
  flex-direction: column;
  overflow: clip;
`;
const TopBanner = styled.div `
  height: ${fluid(295, 500)};
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  z-index: 9;

  background-color: ${brand.primary.blue};
  color: ${brand.white};
`;
const BackgroundElement = styled.div `
  position: absolute;
  right: 0;
  top: 0;
  height: 100%;
  width: ${fluid(237, 400)};
  border-radius: 500px 0px 0px 500px;
  background-color: ${brand.base.blue};
`;
const BannerContent = styled.div `
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;
const Title = styled.div `
  ${h3}
  margin-bottom: ${fluid(10, 30)};

  span {
    font-weight: 600;
  }
`;
const SubTitle = styled.div `
  ${paragraphLarge}
`;
const ListingsSection = styled(SectionWrapper) `
  background-color: ${brand.primary.blueGrey};
  display: flex;
  flex-direction: column;
  position: relative;

  ${SectionWrapperStyles.Inner} {
    @media ${from(Device.TabletLarge)} {
      max-width: 1316px;
    }
  }
`;
const FiltersTop = styled.div `
  padding-bottom: 24px;
  display: flex;
  justify-content: space-between;

  @media ${from(Device.TabletLarge)} {
    border-bottom: 2px solid ${brand.primary.blue};
  }

  ${FiltersStyles.Container} {
    margin: 0;

    @media ${from(Device.TabletLarge)} {
      ${DropdownStyles.Container} {
        flex-basis: 100%;
      }
    }

    @media ${from(Device.TabletLarge)} {
      ${DropdownStyles.Select} {
        background-color: ${brand.white};
      }
    }
  }

  @media ${until(Device.TabletLarge)} {
    flex-direction: column;
    gap: 33px;

    ${FiltersStyles.Container} {
      ${DropdownStyles.Container} {
        flex-basis: 100%;
      }
    }
  }
`;
const TabFilters = styled.div `
  display: flex;
  gap: 16px;

  @media ${from(Device.TabletLarge)} {
    flex-wrap: wrap;
  }

  @media ${until(Device.TabletLarge)} {
    order: 1;
    overflow-x: auto;
    margin-left: calc(-1 * var(--sitePadding));
    margin-right: calc(-1 * var(--sitePadding));
    padding: 0px var(--sitePadding);
  }
`;
const FilterPill = styled.button `
  --colorBgActive: ${brand.primary.blue};
  --bgSize: 0% 100%;
  --bgSizeActive: 100% 100%;

  ${fonts.apparat.semiBold}

  border: none;
  padding: 18px 32px;
  color: ${brand.black};
  text-transform: uppercase;
  /* background-color: ${brand.white}; */
  background: ${brand.white};
  background-image: linear-gradient(to right, var(--colorBgActive), var(--colorBgActive));
  background-repeat: no-repeat;
  background-size: var(--bgSize);
  border-radius: 100px;
  cursor: pointer;
  user-select: none;
  ${transition('color, background-size', 0.15, 'ease-in-out')}

  &:hover {
    background-size: var(--bgSizeActive);
    color: ${brand.white};
  }

  &:focus {
    outline: auto;
    outline-offset: 5px;
  }

  @media ${until(Device.TabletLarge)} {
    width: max-content;
    white-space: nowrap;
  }

  &[data-active='true'] {
    /* background-color: ${brand.primary.blue}; */
    background-size: var(--bgSizeActive);
    color: ${brand.white};
  }
`;
const ResultsCount = styled.div `
  ${paragraphSmall}
  margin-top: ${fluid(10, 40)};
  margin-bottom: ${fluid(20, 82)};
  font-size: ${fluid(16, 18)};
  color: ${brand.base.blue};
`;
const ResultsListing = styled.div `
  display: flex;
  flex-direction: column;
  gap: 32px;
`;
const Result = styled.a `
  display: flex;
  gap: ${fluid(10, 20)};
  align-items: flex-start;
  padding-bottom: ${fluid(25, 32)};
  border-bottom: 1px solid ${brand.base.blue};
  text-decoration: none;

  &:focus {
    outline: none;
  }

  @media ${until(Device.TabletLarge)} {
    flex-direction: column;
  }

  svg {
    margin-left: auto;
    margin-top: 6px;
    width: 45px;
    flex: 0 0 45px;
    cursor: pointer;
    color: ${brand.primary.blue};
  }
`;
const ResultContent = styled.div `
  display: flex;
  flex-direction: column;
  gap: 5px;
  flex-grow: 1;
`;
const ResultTitle = styled.div `
  ${paragraphMedium}
  ${fonts.lato.semiBold}
  line-height: ${fluid(24, 32)};
  color: ${brand.base.blue};
  margin: 0;

  ${Result}:hover &,
  ${Result}:focus & {
    text-decoration: underline;
  }
`;
const ResultSubtitle = styled.div `
  ${paragraphSmall}
  ${fonts.lato.light}
  color: ${transparentize(0.5, brand.base.blue)};
  margin: 0;
`;
const ResultBody = styled.div `
  ${paragraphMedium}
  ${fonts.lato.regular}
  color: ${brand.base.blue};
  padding-top: ${fluid(5, 15)};
  font-size: ${fluid(14, 18)};
  line-height: ${fluid(20, 24)};
  margin: 0;
`;
const BackgroundCircle = styled.div `
  position: absolute;
  z-index: -1;
  bottom: 0;
  left: 0;
  background-color: ${brand.white};
  width: 200vw;
  aspect-ratio: 1 / 1;
  border-radius: 50%;
  transform: translate(-40%, 40%);

  @media ${from(Device.TabletLarge)} {
    transform: translate(-25%, 40%);
    width: 75vw;
  }
`;
const SearchWrapper = styled.div `
  display: flex;
  flex-direction: column;
  gap: 16px;
  text-align: center;
  width: 100%;
  padding-top: ${fluid(20, 45)};
  width: ${fluid(341, 576)};

  span {
    ${paragraphSmall};
    margin: 0;
  }
`;
const PaginationWrapper = styled.div `
  display: flex;
  flex-direction: column;
  gap: ${fluid(30, 50)};
  padding-top: ${fluid(30, 60)};

  span {
    color: ${brand.base.blue};
    text-align: center;
    font-size: ${fluid(14, 18)};
    line-height: ${fluid(20, 24)};
  }
`;
export const SearchResultsStyles = {
    Container,
    TopBanner,
    BackgroundElement,
    BannerContent,
    Title,
    SubTitle,
    ListingsSection,
    FiltersTop,
    TabFilters,
    FilterPill,
    ResultsCount,
    ResultsListing,
    Result,
    ResultContent,
    ResultTitle,
    ResultSubtitle,
    ResultBody,
    BackgroundCircle,
    SearchWrapper,
    PaginationWrapper,
};
